// The Street: capital-markets screen (design §13 screen 8). Pre-IPO it is the
// S-1 desk — eligibility checklist, underwriter choice, process tracker, and
// the pricing-night decision. Post-IPO it is the public-company cockpit:
// price/market-cap chart, guidance dial, credibility, secondaries, buybacks.

import { useState } from "react";
import type { ScreenProps } from "./shared.js";
import { interpolate } from "../../engine/calendar.js";
import { ipoEligibility } from "../../engine/systems/ipo.js";
import { totalShares, ttmEarnings } from "../../engine/systems/finance.js";
import { money, monthLabel, pct } from "../format.js";
import { Bar, MoneyInput, Sparkline } from "../components/widgets.js";

export function Street({ snap, content, session }: ScreenProps) {
  const { view } = snap;
  if (view.street) return <PublicCompany snap={snap} content={content} session={session} />;
  return <IpoDesk snap={snap} content={content} session={session} />;
}

// ---------------------------------------------------------------------------
function IpoDesk({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const cfg = content.economy.ipo;
  const m = view.monthIndex;
  const proc = view.ipo.process;
  const elig = ipoEligibility(view, cfg);
  const s1Queued = pending.some((c) => c.type === "fileS1");
  const priceQueued = pending.find((c) => c.type === "priceIpo");
  const [tier, setTier] = useState<"bulge" | "boutique">("bulge");
  const [priceFrac, setPriceFrac] = useState(0.5);

  const check = (ok: boolean) => (ok ? <span style={{ color: "var(--ink)" }}>✓</span> : <span className="bad-text">✗</span>);

  return (
    <div>
      <h2>THE STREET — PRIVATE COMPANY</h2>

      <div className="row">
        <div className="tile">
          <div className="k">IPO WINDOW</div>
          <div className={`v${elig.window < cfg.windowFloorToFile ? " bad" : elig.window < 0.55 ? " warn" : ""}`}>
            {pct(elig.window, 0)}
          </div>
          <Bar frac={elig.window} mark={cfg.windowFloorToFile} amber={elig.window < cfg.windowFloorToFile} />
          <div className="sub">macro cycle decides when the door is open</div>
        </div>
        <div className="tile">
          <div className="k">STAGE</div>
          <div className="v">{view.stage.toUpperCase()}</div>
          <div className="sub">{view.stage === "growth" ? "underwriters will take the call" : view.stage === "ipo" ? "in process" : "S-1 requires the growth stage"}</div>
        </div>
        <div className="tile">
          <div className="k">PULLED IPOS</div>
          <div className="v">{view.ipo.pulls}</div>
          <div className="sub">{view.ipo.cooldownMonths > 0 ? `street cooling off: ${view.ipo.cooldownMonths}mo` : "the street forgets, slowly"}</div>
        </div>
      </div>

      {proc ? (
        <div className="panel">
          <div className="title">IPO IN PROCESS — {proc.phase.toUpperCase()}</div>
          {proc.phase === "s1" && (
            <div>
              S-1 drafting with {proc.underwriter === "bulge" ? "the bulge-bracket syndicate" : "your boutique bank"} — effective in ~
              {proc.monthsLeft} month{proc.monthsLeft === 1 ? "" : "s"}. Quiet period: no promises you can't file.
            </div>
          )}
          {proc.phase === "roadshow" && (
            <div>
              Roadshow — {proc.monthsLeft} month{proc.monthsLeft === 1 ? "" : "s"} of pitching. Management is on planes and engineering
              runs {pct(cfg.roadshowProductivityHit, 0)} slower. If the window drops under {pct(cfg.pullFloor, 0)}, the deal is pulled.
            </div>
          )}
          {proc.phase === "pricing" && proc.rangeLo !== null && proc.rangeHi !== null && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <b>PRICING NIGHT.</b> The book supports ${proc.rangeLo.toFixed(2)}–${proc.rangeHi.toFixed(2)} per share. Price low for a
                first-day pop (analysts love you, credibility starts higher); price high for proceeds (and risk breaking the issue).
                Undecided for {proc.monthsLeft} more month{proc.monthsLeft === 1 ? "" : "s"} → the bankers price the mid for you.
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={priceFrac * 100}
                  onChange={(e) => setPriceFrac(Number(e.target.value) / 100)}
                  style={{ width: 220 }}
                />
                <span>
                  ${(proc.rangeLo + priceFrac * (proc.rangeHi - proc.rangeLo)).toFixed(2)}
                  {" · "}est. proceeds{" "}
                  {money(
                    (proc.rangeLo + priceFrac * (proc.rangeHi - proc.rangeLo)) *
                      Math.round((cfg.newSharesFrac / (1 - cfg.newSharesFrac)) * totalShares(view.finance)) *
                      (1 - cfg.underwriters[proc.underwriter].feePct),
                  )}
                </span>
                <button
                  className={`act warn${priceQueued ? " queued" : ""}`}
                  onClick={() =>
                    session.queue({ type: "priceIpo", perShare: proc.rangeLo! + priceFrac * (proc.rangeHi! - proc.rangeLo!) })
                  }
                >
                  {priceQueued ? "PRICE QUEUED" : "PRICE IT"}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="panel">
          <div className="title">S-1 ELIGIBILITY — {monthLabel(m)}</div>
          <div className="scroll-x">
            <table>
              <tbody>
                <tr>
                  <td>{check(view.stage === "growth")} growth stage</td>
                  <td className="r">{view.stage}</td>
                </tr>
                <tr>
                  <td>{check(elig.windowOk)} window ≥ {pct(cfg.windowFloorToFile, 0)}</td>
                  <td className="r">{pct(elig.window, 0)}</td>
                </tr>
                <tr>
                  <td>{check(elig.revenueOk)} TTM revenue ≥ {money(elig.minTtmRevenue)}</td>
                  <td className="r">{money(elig.ttmRevenue)}</td>
                </tr>
                <tr>
                  <td>
                    {check(elig.profitOk)} {elig.reqProfitQuarters} consecutive profitable quarter{elig.reqProfitQuarters === 1 ? "" : "s"}
                    {elig.reqProfitQuarters === 0 ? " (the bubble asks for a story, not profits)" : ""}
                  </td>
                  <td className="r">{elig.profitQuarters} qtrs</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", flex: "1 1 280px", minWidth: 0, maxWidth: "100%" }}>
              underwriter{" "}
              <select value={tier} onChange={(e) => setTier(e.target.value as "bulge" | "boutique")} style={{ flex: "1 1 auto", minWidth: 0 }}>
                <option value="bulge">bulge bracket — {pct(cfg.underwriters.bulge.feePct, 1)} fee, best multiple & coverage</option>
                <option value="boutique">boutique — {pct(cfg.underwriters.boutique.feePct, 1)} fee, cheaper, less pull</option>
              </select>
            </label>
            <button
              className={`act warn${s1Queued ? " queued" : ""}`}
              disabled={!(view.stage === "growth" && elig.ok && view.ipo.cooldownMonths <= 0)}
              onClick={() => session.queue({ type: "fileS1", underwriter: tier })}
            >
              {s1Queued ? "S-1 QUEUED" : `FILE THE S-1 (${money(interpolate(cfg.s1Cost, m))})`}
            </button>
            <span className="hint">
              then ~{cfg.s1Months}mo S-1 + {cfg.roadshowMonths}mo roadshow · sells {pct(cfg.newSharesFrac, 0)} of the company · lockup{" "}
              {cfg.lockupMonths}mo
            </span>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="title">WHY GO PUBLIC</div>
        <span className="hint">
          Proceeds without a lead investor's permission, and big-OEM procurement trusts a public balance sheet (+
          {pct(content.economy.models.publicCredibilityShareBonus, 0)} share target while listed). The bill: underwriting fees,{" "}
          {money(interpolate(content.economy.street.publicCostMonthly, m))}/mo of public-company overhead (tripling at SOX in 2002), the
          quarterly guidance treadmill, and a delisting clock if the stock goes under a dollar. File into a closing window and the pull
          costs you the S-1 money, the roadshow slip, and the board's patience.
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PublicCompany({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const street = view.street!;
  const cfg = content.economy.street;
  const m = view.monthIndex;
  const total = totalShares(view.finance);
  const cap = street.sharePrice * total;
  const maxSecondary = Math.round(cap * cfg.secondaryMaxCapFrac);
  const [secondaryAsk, setSecondaryAsk] = useState(50_000_000);
  const [buybackAsk, setBuybackAsk] = useState(20_000_000);
  const guidanceQueued = pending.find((c) => c.type === "setGuidance");
  const policyShown = guidanceQueued && guidanceQueued.type === "setGuidance" ? guidanceQueued.policy : street.guidancePolicy;
  const windowNow = interpolate(content.economy.ipo.window, m);
  const ttmProfit = ttmEarnings(view.kpiHistory);
  const peBinds =
    interpolate(cfg.peMultiple, m) * Math.max(0, ttmProfit) >
    interpolate(cfg.psMultiple, m) * view.finance.ttmRevenue.reduce((a, b) => a + b, 0);

  return (
    <div>
      <h2>THE STREET — {monthLabel(m)}</h2>

      <div className="row">
        <div className="tile">
          <div className="k">SHARE PRICE</div>
          <div className={`v${street.sharePrice < cfg.delistPrice ? " bad" : ""}`}>${street.sharePrice.toFixed(2)}</div>
          <Sparkline data={street.priceHistory.slice(-60).map((p) => p.price)} />
        </div>
        <div className="tile">
          <div className="k">MARKET CAP</div>
          <div className="v">{money(cap)}</div>
          <div className="sub">your stake: {money(cap * view.finance.founderShare)} ({pct(view.finance.founderShare, 1)})</div>
        </div>
        <div className="tile">
          <div className="k">GUIDANCE CREDIBILITY</div>
          <div className={`v${street.credibility < 0.35 ? " bad" : street.credibility < 0.5 ? " warn" : ""}`}>{pct(street.credibility, 0)}</div>
          <Bar frac={street.credibility} amber={street.credibility < 0.5} />
          <div className="sub">{street.consecutiveMisses > 0 ? `${street.consecutiveMisses} consecutive miss${street.consecutiveMisses === 1 ? "" : "es"}` : "beats compound, misses spiral"}</div>
        </div>
        <div className="tile">
          <div className="k">THE QUARTER</div>
          <div className="v">{street.guidedQuarterRevenue !== null ? money(street.guidedQuarterRevenue) : "—"}</div>
          <div className="sub">guided revenue · last qtr {money(street.lastQuarterRevenue)}</div>
        </div>
      </div>

      {street.belowListingMonths > 0 && (
        <div className="panel">
          <div className="title">⚠ LISTING COMPLIANCE</div>
          <span className="bad-text">
            {street.belowListingMonths} month{street.belowListingMonths === 1 ? "" : "s"} under ${cfg.delistPrice.toFixed(2)} — delisted at{" "}
            {cfg.delistMonths}. Buybacks, guidance you can beat, or revenue are the ways out.
          </span>
        </div>
      )}

      {view.ipo.lockupMonth !== null && (
        <div className="panel">
          <div className="title">LOCKUP</div>
          <span className="hint">
            Insider lockup expires {monthLabel(view.ipo.lockupMonth)} — expect one-time selling pressure (~
            {pct(1 - content.economy.ipo.lockupPressure, 0)}).
          </span>
        </div>
      )}

      <div className="panel">
        <div className="title">GUIDANCE POLICY — the beat-and-raise treadmill</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>sandbag</span>
          <input
            type="range"
            min={-100}
            max={100}
            value={Math.round(policyShown * 100)}
            onChange={(e) => session.queue({ type: "setGuidance", policy: Number(e.target.value) / 100 })}
            style={{ width: 220 }}
          />
          <span>stretch</span>
          <span className={guidanceQueued ? "warn-text" : ""}>
            {policyShown > 0 ? "+" : ""}
            {(policyShown * 100).toFixed(0)}% {guidanceQueued ? "(queued)" : ""}
          </span>
        </div>
        <span className="hint">
          Stretch guidance is priced into the stock NOW (+{pct(cfg.guideFairValueCoeff, 0)} of guided growth) and collected at the
          earnings call: beat ⇒ +credibility, +{pct(cfg.beatPricePop, 0)} pop; miss ⇒ −credibility, −{pct(cfg.missPricePenalty, 0)} and
          the board remembers.
        </span>
      </div>

      <div className="row">
        <div className="panel" style={{ flex: 1 }}>
          <div className="title">SECONDARY OFFERING</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>
              $<MoneyInput value={secondaryAsk} onChange={setSecondaryAsk} min={1_000_000} max={maxSecondary} />
            </label>
            <button
              className={`act${pending.some((c) => c.type === "secondaryOffering") ? " queued" : ""}`}
              disabled={view.finance.secondaryCooldownMonths > 0 || windowNow < cfg.secondaryWindowFloor}
              onClick={() => session.queue({ type: "secondaryOffering", amount: secondaryAsk })}
            >
              SELL NEW SHARES
            </button>
          </div>
          <span className="hint">
            {view.finance.secondaryCooldownMonths > 0
              ? `market cooling off: ${view.finance.secondaryCooldownMonths}mo`
              : windowNow < cfg.secondaryWindowFloor
                ? "window shut — no appetite for new paper"
                : `${pct(cfg.secondaryDiscount, 0)} discount + ${pct(cfg.secondaryFee, 0)} fees · dilutes everyone · market absorbs ≤ ${money(maxSecondary)}`}
          </span>
        </div>
        <div className="panel" style={{ flex: 1 }}>
          <div className="title">BUYBACK</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>
              $<MoneyInput value={buybackAsk} onChange={setBuybackAsk} min={1_000_000} />
            </label>
            <button
              className={`act${pending.some((c) => c.type === "buyback") ? " queued" : ""}`}
              disabled={view.cash < buybackAsk}
              onClick={() => session.queue({ type: "buyback", amount: buybackAsk })}
            >
              RETIRE FLOAT
            </button>
          </div>
          <span className="hint">cash for concentration — the anti-dilution lever (and a delisting-fight tool)</span>
        </div>
      </div>

      <div className="panel">
        <div className="title">WHAT SETS THE PRICE</div>
        <span className="hint">
          Fair value = the bigger story: sales — P/S ({interpolate(cfg.psMultiple, m).toFixed(1)}× now) × TTM revenue — or earnings — P/E (
          {interpolate(cfg.peMultiple, m).toFixed(0)}× now) × TTM profit. Right now the {peBinds ? "EARNINGS" : "SALES"} story binds
          {ttmProfit > 0 ? ` (TTM profit ${money(ttmProfit)})` : " (no TTM profit to price)"}. That story × sentiment × credibility ×
          guided growth + cash, all over {total.toLocaleString("en-US")} shares. The price walks {pct(cfg.priceReversion, 0)}/mo toward it
          with noise. Public-company overhead: {money(interpolate(cfg.publicCostMonthly, m))}/mo
          {m < 174 ? " (triples at SOX, Jul 2002)" : " (the SOX era)"}.
        </span>
      </div>
    </div>
  );
}
